/**
 * Boundary parsers. Every byte entering a process crosses one of these.
 *
 * Rules:
 *   - Never throws. Failure ⇒ `{ ok: false, error: ParseError[] }`.
 *   - Every error carries a JSON-pointer-ish `path` naming the bad field.
 *   - Unknown discriminant is a first-class error (not an exception).
 *   - Serialize helpers are ordinary functions (not Result); in dev/test
 *     builds they re-parse before returning to catch builder drift.
 */

import type { z } from "zod";
import type { ParseError, Result } from "./errors.js";
import {
  CLIENT_MESSAGE_SCHEMA,
  SERVER_MESSAGE_SCHEMA,
  type ClientMessage,
  type ServerMessage,
} from "./messages.js";
import { REPLAY_SCHEMA, type Replay } from "./replay.js";
import {
  ABILITY_SCHEMA,
  AURA_DEF_SCHEMA,
  CHAMPION_SCHEMA,
  MAP_SCHEMA,
  TUNING_SCHEMA,
  type AbilityDef,
  type AuraDef,
  type ChampionDef,
  type PitMap,
  type Tuning,
} from "./content-schemas.js";

/** Convert a zod ZodError to `ParseError[]`. Preserves path + message. */
function toParseErrors(err: z.ZodError): ParseError[] {
  return err.issues.map((iss) => ({
    path: iss.path.join("."),
    message: iss.message,
  }));
}

/**
 * Generic Result-returning parse over any schema. Not exported: used
 * internally to build the per-type parsers below with consistent shape.
 */
function parseWith<T>(schema: z.ZodType<T>, raw: unknown): Result<T, ParseError[]> {
  const r = schema.safeParse(raw);
  if (r.success) return { ok: true, value: r.data };
  return { ok: false, error: toParseErrors(r.error) };
}

// ─── Per-type parsers ──────────────────────────────────────────────────────

export function parseClientMessage(raw: unknown): Result<ClientMessage, ParseError[]> {
  return parseWith(CLIENT_MESSAGE_SCHEMA, raw);
}

export function parseServerMessage(raw: unknown): Result<ServerMessage, ParseError[]> {
  return parseWith(SERVER_MESSAGE_SCHEMA, raw);
}

export function parseReplay(raw: unknown): Result<Replay, ParseError[]> {
  return parseWith(REPLAY_SCHEMA, raw);
}

export function parseChampion(raw: unknown): Result<ChampionDef, ParseError[]> {
  return parseWith(CHAMPION_SCHEMA, raw);
}

/**
 * Parse a single standalone ability DEF (#190) from raw content. Same
 * never-throws boundary contract as the champion / map / tuning parsers. Used by
 * `@pit/content` when loading `RAW_ABILITIES`; the parsed def is registry-keyed
 * by `id` and referenced from a champion's `abilities` id-list.
 */
export function parseAbility(raw: unknown): Result<AbilityDef, ParseError[]> {
  return parseWith(ABILITY_SCHEMA, raw);
}

export function parsePitMap(raw: unknown): Result<PitMap, ParseError[]> {
  return parseWith(MAP_SCHEMA, raw);
}

export function parseTuning(raw: unknown): Result<Tuning, ParseError[]> {
  return parseWith(TUNING_SCHEMA, raw);
}

/**
 * Parse a single `AuraDef` (#30) from raw content. Same never-throws
 * boundary contract as the champion / map / tuning parsers.
 *
 * Used by `@pit/content` when loading `RAW_AURAS`. The cross-validator
 * consumes the parsed defs to check `applyAura` references + periodic
 * cadence.
 */
export function parseAuraDef(raw: unknown): Result<AuraDef, ParseError[]> {
  return parseWith(AURA_DEF_SCHEMA, raw);
}

// ─── Serializers ───────────────────────────────────────────────────────────
// Ordinary functions returning JSON string. In dev/test the caller can
// enable a re-parse check by invoking the paired `serializeXStrict` shim.

const DEV_STRICT_SERIALIZE = process.env["NODE_ENV"] !== "production";

/** Serialize a typed value, and in non-production re-parse to detect drift. */
function serializeChecked<T>(
  schema: z.ZodType<T>,
  value: T,
  kind: string,
): string {
  const json = JSON.stringify(value);
  if (DEV_STRICT_SERIALIZE) {
    // Re-parse the exact bytes we intend to send. Any drift = programmer
    // bug in the builder, not in the parser.
    const roundTripped: unknown = JSON.parse(json);
    const check = schema.safeParse(roundTripped);
    if (!check.success) {
      throw new Error(
        `serialize${kind}: built value fails its own schema — ` +
          JSON.stringify(check.error.issues),
      );
    }
  }
  return json;
}

export function serializeClientMessage(msg: ClientMessage): string {
  return serializeChecked(CLIENT_MESSAGE_SCHEMA, msg, "ClientMessage");
}

export function serializeServerMessage(msg: ServerMessage): string {
  return serializeChecked(SERVER_MESSAGE_SCHEMA, msg, "ServerMessage");
}

export function serializeReplay(r: Replay): string {
  return serializeChecked(REPLAY_SCHEMA, r, "Replay");
}
